import { useEffect, useState } from "react"
import { useNavigate, useParams, Link } from "react-router-dom"
import { jwtDecode } from "jwt-decode"

// @mui material components
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import MenuItem from "@mui/material/MenuItem"
import Select from "@mui/material/Select"

// Material Dashboard 2 React components
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDButton from "components/MDButton"
import MDInput from "components/MDInput"

// Material Dashboard 2 React example components
import DashboardLayout from "monitor/LayoutContainers/DashboardLayout"
import DashboardNavbar from "monitor/Navbars/DashboardNavbar"
import Footer from "monitor/Footer"

// SweetAlert
import { showToast } from "utils/swalToast"

// Services
import {
  fetchSucursales,
  fetchDependencias,
  fetchTipoAsistenteById,
  createTipoAsistente,
  updateTipoAsistente,
} from "./service"

function TipoAsistenteAdd() {
  const { id } = useParams()
  const navigate = useNavigate()
  const token = (sessionStorage.getItem("token") || localStorage.getItem("token"))
  const isEditMode = Boolean(id)

  // State for form data
  const [formData, setFormData] = useState({
    id: null,
    dependenciaId: "",
    nombre: "",
  })

  // State for dropdowns
  const [sucursales, setSucursales] = useState([])
  const [dependencias, setDependencias] = useState([])
  const [selectedSucursal, setSelectedSucursal] = useState("")

  // State for loading
  const [loading, setLoading] = useState(false)
  const [loadingOrganizaciones, setLoadingOrganizaciones] = useState(false)

  // Verify token
  useEffect(() => {
    if (!token) {
      navigate("/authentication/sign-in", { replace: true })
      return
    }
    try {
      const { exp } = jwtDecode(token)
      const nowSec = Math.floor(Date.now() / 1000)
      if (!exp || exp < nowSec) {
        navigate("/authentication/sign-in", { replace: true })
      }
    } catch {
      navigate("/authentication/sign-in", { replace: true })
    }
  }, [token, navigate])

  // Load initial data
  useEffect(() => {
    loadSucursales()
  }, [])

  // Load data if editing
  useEffect(() => {
    if (isEditMode && id) {
      loadTipoAsistenteData()
    }
  }, [isEditMode, id])

  // Load dependencias when sucursal changes
  useEffect(() => {
    if (selectedSucursal) {
      loadDependencias()
    } else {
      setDependencias([])
      setFormData((prev) => ({ ...prev, dependenciaId: "" }))
    }
  }, [selectedSucursal])

  const loadSucursales = async () => {
    try {
      const data = await fetchSucursales()
      setSucursales(data || [])
    } catch (error) {
      console.error("Error loading sucursales:", error)
    }
  }

  const loadDependencias = async () => {
    setLoadingOrganizaciones(true)
    try {
      const data = await fetchDependencias({ sucursalId: selectedSucursal })
      setDependencias(data || [])
    } catch (error) {
      console.error("Error loading dependencias:", error)
    } finally {
      setLoadingOrganizaciones(false)
    }
  }

  const loadTipoAsistenteData = async () => {
    setLoading(true)
    try {
      const data = await fetchTipoAsistenteById(id)
      setFormData({
        id: data.id,
        dependenciaId: data.dependenciaId || "",
        nombre: data.nombre || "",
      })

      // Load dependencias for the edit mode
      if (data.dependenciaId) {
        // We need to find which sucursal this organization belongs to
        const allOrgs = await fetchDependencias({})
        const org = allOrgs.find((o) => o.id === data.dependenciaId)
        if (org && org.sucursalId) {
          setSelectedSucursal(org.sucursalId)
        }
      }
    } catch (error) {
      console.error("Error loading tipo de asistente:", error)
      showToast({ icon: "error", title: "Error", text: "No se pudo cargar el tipo de asistente" })
      navigate("/asistencia/tipos-asistente")
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Validations
    if (!formData.dependenciaId) {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor seleccione una dependencia" })
      return
    }

    if (!formData.nombre || formData.nombre.trim() === "") {
      showToast({ icon: "warning", title: "Campo requerido", text: "Por favor ingrese el nombre del tipo de asistente" })
      return
    }

    setLoading(true)
    try {
      const dataToSend = {
        id: formData.id,
        dependenciaId: formData.dependenciaId,
        nombre: formData.nombre.trim(),
      }

      if (isEditMode) {
        await updateTipoAsistente(id, dataToSend)
      } else {
        await createTipoAsistente(dataToSend)
      }

      navigate("/asistencia/tipos-asistente")
    } catch (error) {
      console.error("Error saving tipo de asistente:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={3} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={2}>
                <MDTypography variant="h5">
                  {isEditMode ? "Editar tipo de asistente" : "Agregar tipo de asistente"}
                </MDTypography>
              </MDBox>
              <MDBox pt={2} px={2} component="form" role="form" onSubmit={handleSubmit}>
                {/* Sucursal */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Sucursal</InputLabel>
                    <Select
                      label="Sucursal"
                      value={selectedSucursal}
                      onChange={(e) => setSelectedSucursal(e.target.value)}
                      required
                      disabled={loading}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione una sucursal</MenuItem>
                      {sucursales.map((suc) => (
                        <MenuItem key={suc.idSucursal} value={suc.idSucursal}>
                          {suc.descripcion}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Dependencia */}
                <MDBox mb={2}>
                  <FormControl fullWidth>
                    <InputLabel>Dependencia</InputLabel>
                    <Select
                      label="Dependencia"
                      name="dependenciaId"
                      value={formData.dependenciaId}
                      onChange={handleInputChange}
                      required
                      disabled={!selectedSucursal || loadingOrganizaciones || loading}
                      sx={{ padding: "10px" }}
                    >
                      <MenuItem value="">Seleccione una dependencia</MenuItem>
                      {dependencias.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                          {org.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </MDBox>

                {/* Nombre */}
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    name="nombre"
                    label="Nombre del Tipo de Asistente"
                    fullWidth
                    required
                    value={formData.nombre}
                    onChange={handleInputChange}
                    disabled={loading}
                  />
                </MDBox>

                {/* Botones */}
                <MDBox mb={2} sx={{ textAlign: "center" }}>
                  <Link to="/asistencia/tipos-asistente">
                    <MDButton variant="outlined" color="info" sx={{ mr: 1, width: 90 }} disabled={loading}>
                      Cancelar
                    </MDButton>
                  </Link>
                  <MDButton type="submit" variant="gradient" color="info" sx={{ ml: 1, width: 90 }} disabled={loading}>
                    {loading ? "..." : isEditMode ? "Actualizar" : "Agregar"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
}

export default TipoAsistenteAdd
